import React, {useState} from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LanguageIcon from '@mui/icons-material/Language';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import {useNavigate, useParams} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {GET_DOCUMENT_GROUP} from '../graphql/queries';
import {
  REMOVE_GROUP_MEMBER,
  GENERATE_GUIDE_FROM_GROUP,
  REGENERATE_GUIDE_FROM_GROUP,
  DELETE_DOCUMENT_GROUP,
  UPDATE_DOCUMENT_GROUP,
} from '../graphql/mutations';
import {useAuth} from '../contexts/AuthContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {colors} from '../theme/colors';

function formatRelativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

function sourceTypeIcon(type: string) {
  switch (type) {
    case 'pdf': return <PictureAsPdfIcon sx={{fontSize: 20, color: colors.red[600]}} />;
    case 'url':
    case 'html': return <LanguageIcon sx={{fontSize: 20, color: colors.blue[500]}} />;
    case 'image': return <ImageIcon sx={{fontSize: 20, color: colors.purple[500]}} />;
    default: return <DescriptionIcon sx={{fontSize: 20, color: colors.gray[500]}} />;
  }
}

function extractionStatusChip(status: string) {
  switch (status) {
    case 'COMPLETED':
      return <Chip label="Extracted" size="small" color="success" variant="outlined" sx={{height: 20, fontSize: 11}} />;
    case 'PROCESSING':
      return <Chip label="Processing" size="small" color="info" variant="outlined" sx={{height: 20, fontSize: 11}} />;
    case 'FAILED':
      return <Chip label="Failed" size="small" color="error" variant="outlined" sx={{height: 20, fontSize: 11}} />;
    default:
      return <Chip label="Pending" size="small" variant="outlined" sx={{height: 20, fontSize: 11}} />;
  }
}

export default function DocumentGroupDetailPage() {
  useDocumentTitle('Document Groups', 'Group Detail');

  const {user} = useAuth();
  const navigate = useNavigate();
  const {groupId} = useParams<{groupId: string}>();

  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info'}>({
    open: false, message: '', severity: 'info',
  });
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const {data, loading, refetch} = useQuery(GET_DOCUMENT_GROUP, {
    variables: {id: groupId},
    skip: !groupId || !user,
    fetchPolicy: 'network-only',
  });

  const [removeMember] = useMutation(REMOVE_GROUP_MEMBER);
  const [generateGuide, {loading: generating}] = useMutation(GENERATE_GUIDE_FROM_GROUP);
  const [regenerateGuide, {loading: regenerating}] = useMutation(REGENERATE_GUIDE_FROM_GROUP);
  const [deleteGroup] = useMutation(DELETE_DOCUMENT_GROUP);
  const [updateGroup] = useMutation(UPDATE_DOCUMENT_GROUP);

  const group = data?.documentGroup;
  const members = group?.members || [];
  const linkedGuide = group?.linkedGuide;
  const isOutOfDate = linkedGuide?.isOutOfDate;
  const isPublished = !!linkedGuide?.publishedAt;
  const hasGuide = !!linkedGuide;

  // --- Handlers ---

  const handleRemoveMember = async (membershipId: string) => {
    try {
      await removeMember({variables: {membershipId}});
      setSnackbar({open: true, message: 'Source removed from group', severity: 'success'});
      refetch();
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Failed to remove', severity: 'error'});
    }
  };

  const handleGenerate = async () => {
    if (!groupId) return;
    try {
      const result = await generateGuide({variables: {groupId}});
      const guideData = result.data?.generateGuideFromGroup;
      if (guideData?.existingGuide && guideData?.guide?.id) {
        setSnackbar({open: true, message: 'This group already has a guide. Opening it.', severity: 'info'});
        navigate(`/org/guides/${guideData.guide.id}/edit`);
      } else if (guideData?.generationJobId) {
        setSnackbar({open: true, message: 'Guide generation started...', severity: 'info'});
        navigate(`/org/jobs/${guideData.generationJobId}`);
      }
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Generation failed', severity: 'error'});
    }
  };

  const handleRegenerate = async () => {
    if (!linkedGuide?.id) return;
    try {
      const result = await regenerateGuide({variables: {guideId: linkedGuide.id}});
      const regenData = result.data?.regenerateGuideFromGroup;
      if (regenData?.generationJobId) {
        setSnackbar({open: true, message: 'Guide regeneration started...', severity: 'info'});
        navigate(`/org/jobs/${regenData.generationJobId}`);
      } else {
        setSnackbar({open: true, message: 'Guide regenerated', severity: 'success'});
        refetch();
      }
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Regeneration failed', severity: 'error'});
    }
  };

  const handleOpenGuide = () => {
    if (linkedGuide?.id) {
      navigate(`/org/guides/${linkedGuide.id}/edit`);
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupId) return;
    try {
      await deleteGroup({variables: {id: groupId}});
      navigate('/org/groups');
    } catch (err: any) {
      setConfirmDeleteOpen(false);
      setSnackbar({open: true, message: err.message || 'Failed to delete', severity: 'error'});
    }
  };

  const handleSaveName = async () => {
    if (!groupId || !nameValue.trim()) return;
    try {
      await updateGroup({variables: {id: groupId, name: nameValue.trim()}});
      setEditingName(false);
      refetch();
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Failed to update', severity: 'error'});
    }
  };

  // --- Loading / error ---

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!group) {
    return (
      <Box sx={{py: 4, px: 3}}>
        <Typography sx={{fontSize: 20, fontWeight: 600}}>Group not found</Typography>
        <Button onClick={() => navigate('/org/groups')} sx={{mt: 1}}>Back to groups</Button>
      </Box>
    );
  }

  // --- Render ---

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Breadcrumb */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
        <IconButton size="small" onClick={() => navigate('/org/groups')}>
          <ArrowBackIcon sx={{fontSize: 20}} />
        </IconButton>
        <Typography
          onClick={() => navigate('/org/groups')}
          sx={{fontSize: 13, color: '#6B7280', cursor: 'pointer', '&:hover': {color: colors.blue[600]}}}
        >
          Document Groups
        </Typography>
        <Typography sx={{fontSize: 13, color: '#9CA3AF'}}>/</Typography>
        <Typography sx={{fontSize: 13, color: colors.textPrimary, fontWeight: 500}}>
          {group.name}
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
        <Box sx={{flex: 1, mr: 2}}>
          {editingName ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <TextField
                autoFocus
                size="small"
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                sx={{fontSize: 24}}
              />
              <Button size="small" onClick={handleSaveName}>Save</Button>
              <Button size="small" onClick={() => setEditingName(false)}>Cancel</Button>
            </Box>
          ) : (
            <Typography
              onClick={() => { setNameValue(group.name); setEditingName(true); }}
              sx={{fontSize: 24, fontWeight: 650, color: colors.textPrimary, cursor: 'pointer', '&:hover': {color: colors.blue[600]}}}
            >
              {group.name}
            </Typography>
          )}
          <Typography sx={{fontSize: 14, color: '#6B7280', mt: 0.5}}>
            {members.length} {members.length === 1 ? 'source' : 'sources'}
            {linkedGuide?.generatedAt && ` \u00b7 Last generated ${formatRelativeTime(linkedGuide.generatedAt)}`}
          </Typography>
        </Box>

        {/* CTA area — one primary action based on state */}
        <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0}}>
          {!hasGuide && (
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
              onClick={handleGenerate}
              disabled={generating || members.length === 0}
            >
              Generate guide
            </Button>
          )}
          {hasGuide && isOutOfDate && (
            <>
              <Button
                variant="contained"
                color="warning"
                startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRegenerate}
                disabled={regenerating}
              >
                Update guide
              </Button>
              <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={handleOpenGuide}>
                Open guide
              </Button>
            </>
          )}
          {hasGuide && !isOutOfDate && (
            <>
              <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={handleOpenGuide}>
                Open guide
              </Button>
              <Tooltip title="Regenerate guide content from current sources">
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRegenerate} disabled={regenerating}>
                  Regenerate
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Out-of-date banner */}
      {hasGuide && isOutOfDate && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{mb: 3, borderRadius: '8px'}}
          action={
            <Button color="inherit" size="small" onClick={handleRegenerate} disabled={regenerating}>
              Update guide
            </Button>
          }
        >
          <Typography sx={{fontSize: 14, fontWeight: 500}}>
            Out of date: sources changed since last generation
          </Typography>
          <Typography sx={{fontSize: 13, color: 'inherit', opacity: 0.8}}>
            The guide was generated from an older version of this group. Update to reflect the latest sources.
          </Typography>
        </Alert>
      )}

      {/* Guide status card */}
      {hasGuide && (
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            p: 2.5,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
              <Typography sx={{fontSize: 14, fontWeight: 600}}>Linked Guide</Typography>
              <Chip
                label={isPublished ? 'Published' : 'Draft'}
                size="small"
                color={isPublished ? 'success' : 'default'}
                variant={isPublished ? 'filled' : 'outlined'}
                sx={{height: 20, fontSize: 11}}
              />
              {isOutOfDate && (
                <Chip
                  label="Out of date"
                  size="small"
                  sx={{height: 20, fontSize: 11, bgcolor: colors.amber[50], color: colors.amber[700], border: `1px solid ${colors.amber[200]}`}}
                />
              )}
            </Box>
            <Typography sx={{fontSize: 16, fontWeight: 500, color: colors.blue[600], cursor: 'pointer', '&:hover': {textDecoration: 'underline'}}} onClick={handleOpenGuide}>
              {linkedGuide.title || 'Untitled guide'}
            </Typography>
            {linkedGuide.steps?.length > 0 && (
              <Typography sx={{fontSize: 13, color: '#9CA3AF', mt: 0.25}}>
                {linkedGuide.steps.length} steps
              </Typography>
            )}
          </Box>
          <Button size="small" variant="text" startIcon={<OpenInNewIcon sx={{fontSize: 14}} />} onClick={handleOpenGuide} sx={{textTransform: 'none'}}>
            Edit guide
          </Button>
        </Box>
      )}

      {/* Sources section */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,.06)',
          overflow: 'hidden',
        }}
      >
        {/* Section header */}
        <Box sx={{px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB'}}>
          <Typography sx={{fontSize: 14, fontWeight: 600}}>
            Sources in this group
            {members.length > 0 && (
              <Chip label={members.length} size="small" sx={{ml: 1, height: 20, fontSize: 12}} />
            )}
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{textTransform: 'none'}} disabled>
            Add sources
          </Button>
        </Box>

        {/* Table header */}
        {members.length > 0 && (
          <Box
            sx={{
              px: 2.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #E5E7EB',
              bgcolor: '#FAFAFA',
            }}
          >
            <Typography sx={{flex: 1, fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
              Source
            </Typography>
            <Typography sx={{width: 80, fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
              Type
            </Typography>
            <Typography sx={{width: 100, fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
              Status
            </Typography>
            <Typography sx={{width: 100, fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
              Updated
            </Typography>
            <Box sx={{width: 40}} />
          </Box>
        )}

        {/* Source rows */}
        {members.map((membership: any) => {
          const item = membership.sourceItem;
          if (!item) return null;
          return (
            <Box
              key={membership.id}
              sx={{
                px: 2.5,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-child': {borderBottom: 'none'},
                '&:hover': {bgcolor: 'action.hover'},
              }}
            >
              <Box sx={{flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0}}>
                {sourceTypeIcon(item.type)}
                <Typography sx={{fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  {item.title}
                </Typography>
              </Box>
              <Typography sx={{width: 80, fontSize: 13, color: '#6B7280', textTransform: 'uppercase'}}>
                {item.type}
              </Typography>
              <Box sx={{width: 100}}>
                {extractionStatusChip(item.extractionStatus)}
              </Box>
              <Typography sx={{width: 100, fontSize: 13, color: '#9CA3AF'}}>
                {formatRelativeTime(item.updatedAt)}
              </Typography>
              <Box sx={{width: 40, textAlign: 'right'}}>
                <Tooltip title="Remove from group">
                  <IconButton size="small" onClick={() => handleRemoveMember(membership.id)}>
                    <DeleteOutlineIcon sx={{fontSize: 18, color: '#9CA3AF'}} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}

        {/* Empty state */}
        {members.length === 0 && (
          <Box sx={{py: 5, textAlign: 'center'}}>
            <Typography sx={{fontSize: 14, color: '#9CA3AF'}}>
              No sources in this group yet
            </Typography>
            <Typography sx={{fontSize: 13, color: '#9CA3AF', mt: 0.5}}>
              Add source documents to generate a guide from them.
            </Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{mt: 2, textTransform: 'none'}} disabled>
              Add sources
            </Button>
          </Box>
        )}
      </Box>

      {/* Danger zone */}
      <Box sx={{mt: 4, display: 'flex', justifyContent: 'flex-end'}}>
        <Button
          size="small"
          color="error"
          variant="text"
          onClick={() => setConfirmDeleteOpen(true)}
          sx={{textTransform: 'none', fontSize: 13}}
        >
          Delete group
        </Button>
      </Box>

      {/* Confirm delete dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontWeight: 600}}>Delete this group?</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 14, color: '#6B7280'}}>
            {hasGuide
              ? 'This group has a linked guide. You must delete the guide first before deleting the group.'
              : `This will permanently delete "${group.name}" and all its memberships. This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} sx={{textTransform: 'none'}}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteGroup}
            disabled={hasGuide}
            sx={{textTransform: 'none'}}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity} sx={{width: '100%'}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
